import type { ReactElement, ReactNode } from 'react'

import { Explicacao, type ExplicacaoProps } from './explicacao'
import { Fonte, type Origem } from './fonte'

import type { Rag } from '../types'

import { Card } from '@/components/ui/card'
import { cn } from '@/lib/cn'

/** Cor do número e da barra. RAG manda; sem meta, fica neutro na cor da marca. */
const TOM: Record<Rag, { texto: string; barra: string }> = {
  verde: { texto: 'text-success', barra: 'bg-success' },
  amarelo: { texto: 'text-warning', barra: 'bg-warning' },
  vermelho: { texto: 'text-destructive', barra: 'bg-destructive' },
  'sem-meta': { texto: 'text-foreground', barra: 'bg-primary' },
}

export interface KpiCardProps {
  rotulo: string
  valor: string
  /** Cadência do indicador (ex.: "mensal"), como no padrão da Camada B. */
  cadencia?: string
  /** Texto curto ao lado do número — o "89% da meta" da referência. */
  destaque?: string
  detalhe?: ReactNode
  /** Origem do dado — vira um selo visível no rodapé do card. */
  origem?: Origem
  rag?: Rag
  /** 0..1 — preenchimento da barra. Sem isso a barra não aparece. */
  progresso?: number
  /** Mini gráfico (ex.: a série por trás de um "médio"), abaixo do número. */
  grafico?: ReactNode
  explicacao?: Omit<ExplicacaoProps, 'rotulo'>
  /**
   * `telao` amplia número, rótulo e respiro. Existe para blocos com poucos
   * indicadores no modo apresentação: no tamanho normal eles ficariam pequenos
   * no meio da tela, porque não há densidade para o palco escalar.
   */
  tamanho?: 'normal' | 'telao'
  className?: string
}

const NUMERO = {
  normal: 'text-3xl sm:text-[2.5rem]',
  telao: 'text-5xl sm:text-6xl',
} as const

const ROTULO = {
  normal: 'text-[11px]',
  telao: 'text-sm',
} as const

const PADDING = {
  normal: 'p-4 sm:p-5',
  telao: 'p-6 sm:p-7',
} as const

const DETALHE = {
  normal: 'text-xs',
  telao: 'text-base',
} as const

export function KpiCard({
  rotulo,
  valor,
  cadencia,
  destaque,
  detalhe,
  origem,
  rag = 'sem-meta',
  progresso,
  grafico,
  explicacao,
  tamanho = 'normal',
  className,
}: KpiCardProps): ReactElement {
  const tom = TOM[rag]

  return (
    <Card className={cn('flex flex-col gap-3', PADDING[tamanho], className)}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1">
          <span
            className={cn(
              'text-muted-foreground truncate font-semibold uppercase tracking-[0.14em]',
              ROTULO[tamanho],
            )}
          >
            {rotulo}
          </span>
          {explicacao ? <Explicacao {...explicacao} rotulo={rotulo} /> : null}
        </div>
        {cadencia ? (
          <span className="border-border text-muted-foreground shrink-0 rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide">
            {cadencia}
          </span>
        ) : null}
      </div>

      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span
          className={cn(
            'font-mono font-semibold leading-none tracking-tight',
            NUMERO[tamanho],
            tom.texto,
          )}
        >
          {valor}
        </span>
        {destaque ? <span className="text-muted-foreground text-sm">{destaque}</span> : null}
      </div>

      {progresso === undefined ? null : (
        <div
          className="bg-muted h-1.5 overflow-hidden rounded-full"
          role="progressbar"
          aria-valuenow={Math.round(progresso * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Progresso de ${rotulo}`}
        >
          <div
            className={cn('h-full rounded-full transition-[width]', tom.barra)}
            style={{ width: `${Math.max(2, Math.min(100, progresso * 100))}%` }}
          />
        </div>
      )}

      {grafico ? <div className="mt-1">{grafico}</div> : null}

      <div
        className={cn(
          'text-muted-foreground mt-auto flex items-end justify-between gap-2',
          DETALHE[tamanho],
        )}
      >
        <span className="min-w-0">{detalhe}</span>
        {origem ? <Fonte origem={origem} campo={explicacao?.origem} /> : null}
      </div>
    </Card>
  )
}
